import { useMemo, useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import {
  Menu,
  X,
  BarChart3,
  Users,
  UserPlus,
  Store,
  Package,
  Settings,
  Mail,
  HelpCircle,
  LogOut,
  Gift,
  Ticket,
  Clock,
} from 'lucide-react'
import { useAuthStore } from '@/modules/auth/stores/authStore'
import { usePermissions } from '@/modules/auth/hooks/usePermissions'
import {
  sidebarConfig,
  type SidebarIconName,
} from '@/modules/auth/permissions/permissions'

// Map icon names (strings) to Lucide components
const iconMap: Record<SidebarIconName, React.ReactNode> = {
  BarChart3: <BarChart3 size={20} />,
  Users: <Users size={20} />,
  UserPlus: <UserPlus size={20} />,
  Store: <Store size={20} />,
  Package: <Package size={20} />,
  Gift: <Gift size={20} />,
  Ticket: <Ticket size={20} />,
  Settings: <Settings size={20} />,
  Mail: <Mail size={20} />,
  HelpCircle: <HelpCircle size={20} />,
  Clock: <Clock size={20} />,
}

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout } = useAuthStore()
  const { hasPermission, role } = usePermissions()
  const navigate = useNavigate()

  // Filter sidebar items based on the user's permissions
  const menuItems = useMemo(
    () =>
      sidebarConfig
        .filter((item) => !item.permission || hasPermission(item.permission))
        .map((item) => ({
          label: item.label,
          path: item.path,
          icon: iconMap[item.icon],
        })),
    [hasPermission],
  )

  const handleLogout = () => {
    logout()
    navigate('/auth/login', { replace: true })
  }

  // Display label for the user's role
  const roleLabel = role === 'Admin' ? 'Admin' : null

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-screen w-64 bg-white border-r border-gray-200
          flex flex-col overflow-y-auto
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 bg-primary-600 shrink-0">
          <span className="text-white font-semibold text-lg">Menu</span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-white hover:text-primary-100 lg:hidden"
          >
            <X size={24} />
          </button>
        </div>

        {/* Nav links */}
        <nav className="p-2 space-y-1 flex-1">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main content area */}
      <div className="flex flex-col min-h-screen lg:ml-64">
        {/* Top toolbar */}
        <header className="sticky top-0 z-20 flex items-center h-16 px-4 bg-primary-600 text-white shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            className="mr-4 hover:text-primary-100 lg:hidden"
          >
            <Menu size={24} />
          </button>

          <div className="flex-1" />

          <span className="text-sm mr-4">
            {user?.rut ?? user?.email}
            {roleLabel && (
              <span className="ml-2 px-2 py-0.5 bg-primary-700 rounded text-xs">
                {roleLabel}
              </span>
            )}
          </span>

          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 text-sm hover:text-primary-100 transition-colors"
          >
            <LogOut size={18} />
            Cerrar sesión
          </button>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
